import type { LaseredCondition } from "@/types";
import type { DeductibleReview, DeductibleReviewCandidate } from "@/types/deductibleReview";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { formatDate } from "@/utils/formatters/dateFormatter";
import { hasLiveExpectedTrigger } from "@/utils/helpers/expectedTrigger";

/**
 * Helper text for an open deductible review (#7345). One builder for the chip
 * tooltip, the claimant banner and the dialog header, so the three never say
 * different things. Reason wording follows the #7345 acceptance criteria.
 */

const money = (n: number | undefined) =>
  n === undefined ? "an unknown amount" : formatUSD(n, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const day = (d: string | undefined) => (d ? formatDate(d.slice(0, 10)) : "an unknown date");

const conditionOf = (c: DeductibleReviewCandidate, byId: Map<string, LaseredCondition>) =>
  c.source === "conditional_laser" && c.source_ref_id ? byId.get(c.source_ref_id) : undefined;

/**
 * A conditional-laser candidate that comes from the condition's live expected
 * trigger (#7361), not a claim line.
 */
export const isExpectedTriggerCandidate = (
  c: DeductibleReviewCandidate,
  conditionsById: Map<string, LaseredCondition>
): boolean => !c.triggering_claim_item_id && hasLiveExpectedTrigger(conditionOf(c, conditionsById));

/**
 * The expected-trigger candidate of an `expected_trigger_overdue` review: the
 * conditional laser whose expected trigger is overdue (or live), else the
 * proposed conditional laser. The other candidate is the claimant's straight
 * laser or the policy base deductible.
 */
export const overdueExpectedCandidate = (
  cands: DeductibleReviewCandidate[],
  conditionsById: Map<string, LaseredCondition>
): DeductibleReviewCandidate | undefined =>
  cands.find((c) => conditionOf(c, conditionsById)?.expected_trigger?.state === "overdue") ??
  cands.find((c) => isExpectedTriggerCandidate(c, conditionsById)) ??
  cands.find((c) => c.source === "conditional_laser" && c.is_proposed);

/** Human name of a candidate: the condition's name for a conditional laser. */
export const candidateLabel = (c: DeductibleReviewCandidate, conditionsById: Map<string, LaseredCondition>): string => {
  switch (c.source) {
    case "conditional_laser": {
      const name = conditionOf(c, conditionsById)?.lasered_condition_name;
      const label = name ? `${name} conditional laser` : "Conditional laser";
      return isExpectedTriggerCandidate(c, conditionsById) ? `${label} (expected trigger)` : label;
    }
    case "lasered":
      return "Straight laser";
    case "modification":
      return "Deductible modification";
    case "specific":
      return "Policy base deductible";
    case "contract_laser":
      return "Contract laser";
    case "contract_excluded":
      return "Contract exclusion";
    case "review_decision":
      return "Earlier review decision";
    case "merged_laser":
      return "Laser from merged claimant";
    default:
      return "Deductible";
  }
};

const HOLD = "Stop-loss claims for this claimant are on hold until resolved.";

/** A generic label inside a sentence: "the straight laser", "the policy base deductible". */
const inSentence = (label: string) => label.charAt(0).toLowerCase() + label.slice(1);

/**
 * A conditional-laser candidate inside a sentence that already says it is the
 * expected trigger: "ESRD conditional laser", or "conditional laser" unnamed.
 */
const conditionalInSentence = (c: DeductibleReviewCandidate, byId: Map<string, LaseredCondition>) => {
  const name = conditionOf(c, byId)?.lasered_condition_name;
  return name ? `${name} conditional laser` : "conditional laser";
};

/**
 * What choosing a candidate of an `expected_trigger_overdue` review does (#7361):
 * a conditional laser keeps its expected trigger (a new review-by date is
 * needed); the other candidate (straight laser or policy base deductible)
 * cancels it. Named with the candidate labels, as the other reviews are.
 */
export const overdueChoiceText = (
  c: DeductibleReviewCandidate,
  cands: DeductibleReviewCandidate[],
  conditionsById: Map<string, LaseredCondition>
): string => {
  if (c.source === "conditional_laser") return "Keeps the expected trigger with a new review-by date.";
  const expected = overdueExpectedCandidate(cands, conditionsById);
  const on = expected ? ` on the ${conditionalInSentence(expected, conditionsById)}` : "";
  return `Choosing the ${inSentence(candidateLabel(c, conditionsById))} cancels the expected trigger${on}.`;
};

const latestDated = (cands: DeductibleReviewCandidate[]) =>
  cands.reduce<DeductibleReviewCandidate | undefined>(
    (best, c) => (!best || c.effective_date > best.effective_date ? c : best),
    undefined
  );

/**
 * The candidate the resolver would apply: the server's `is_proposed`, else the
 * latest-dated conditional laser (the resolver's most-recent-wins rule), so the
 * text names the same term the held deductible will move to.
 */
export const proposedCandidate = (cands: DeductibleReviewCandidate[]): DeductibleReviewCandidate | undefined =>
  cands.find((c) => c.is_proposed) ?? latestDated(cands.filter((c) => c.source === "conditional_laser"));

/** The sentence that explains the conflict; `null` while the review is loading. */
export const reviewHelperText = (review: DeductibleReview | undefined, conditions: LaseredCondition[] = []): string => {
  if (!review) return `Deductible needs review. ${HOLD} Click to review.`;
  const byId = new Map(conditions.map((c) => [c.id, c]));
  const cands = review.candidates ?? [];

  switch (review.reason) {
    case "same_date_tie": {
      const latest = cands.reduce((max, c) => (c.effective_date > max ? c.effective_date : max), "");
      const tied = cands.filter((c) => c.effective_date === latest);
      const conditionsOnly = tied.every((c) => c.source === "conditional_laser");
      const amounts = tied.map((c) => money(c.amt_deductible)).join(" / ");
      const what = conditionsOnly
        ? `${tied.length === 2 ? "two" : tied.length} conditions triggered`
        : "deductibles take effect";
      return `Deductible needs review: ${what} on ${day(latest)} with different deductibles (${amounts}). ${HOLD} Click to review.`;
    }
    case "increase_after_submission": {
      // A reversed lowering trigger can leave just the standing term as the
      // candidate; with no proposal and no condition, take the highest amount.
      const target =
        proposedCandidate(cands) ??
        cands.reduce<DeductibleReviewCandidate | undefined>(
          (max, c) => (!max || c.amt_deductible > max.amt_deductible ? c : max),
          undefined
        );
      return `Deductible needs review: it would increase from ${money(review.amt_held_deductible)} to ${money(
        target?.amt_deductible
      )} after a stop-loss claim was submitted. New stop-loss claims are on hold until you confirm. Click to review.`;
    }
    case "manual_after_trigger": {
      const manual = cands.find((c) => c.source === "lasered" || c.source === "modification");
      const proposed = proposedCandidate(cands);
      const trigger =
        proposed?.source === "conditional_laser"
          ? proposed
          : latestDated(cands.filter((c) => c.source === "conditional_laser"));
      const manualWhat = manual?.source === "modification" ? "a deductible modification" : "a straight laser";
      const triggerName = trigger ? (conditionOf(trigger, byId)?.lasered_condition_name ?? "Conditional laser") : "the";
      return `Deductible needs review: ${manualWhat} of ${money(manual?.amt_deductible)} effective ${day(
        manual?.effective_date
      )} was entered after the ${triggerName} conditional laser (${money(trigger?.amt_deductible)}) triggered on ${day(
        trigger?.effective_date
      )}. The deductible in force before stays while you choose, and new stop-loss claims are on hold. Click to review.`;
    }
    case "expected_trigger_overdue": {
      // #7361: the expected trigger passed its review-by date with no claim line.
      // Worded like the other conditional-laser reviews: the candidates by their labels.
      const expected = overdueExpectedCandidate(cands, byId);
      const cond = expected ? conditionOf(expected, byId) : undefined;
      const laser = expected ? conditionalInSentence(expected, byId) : "conditional laser";
      const what = `the expected trigger on ${cond ? "the" : "a"} ${laser}`;
      const terms = expected
        ? ` (${money(expected.amt_deductible)}${expected.effective_date ? ` from ${day(expected.effective_date)}` : ""})`
        : "";
      const reviewBy = cond?.expected_trigger?.review_by ? ` (${day(cond.expected_trigger.review_by)})` : "";
      const other = cands.find((c) => c.source !== "conditional_laser");
      const choose = other
        ? `choose the ${inSentence(candidateLabel(other, byId))} (${money(other.amt_deductible)}), which cancels the expected trigger`
        : "cancel the expected trigger";
      return `Deductible needs review: ${what}${terms} is past its review-by date${reviewBy} and no qualifying claim line has arrived. Keep the ${laser} with a new review-by date, or ${choose}. The deductible in force stays while you choose, and new stop-loss claims are on hold. Click to review.`;
    }
    case "merged_laser_conflict": {
      const amounts = cands.map((c) => money(c.amt_deductible)).join(" / ");
      return `Deductible needs review: two claimants with different straight lasers were merged (${amounts}). Choose which laser stays. ${HOLD} Click to review.`;
    }
    default:
      return `Deductible needs review. ${HOLD} Click to review.`;
  }
};

/**
 * On a policy with aggregate specific deductible (ASD) allocation the review
 * hold does not stop ASD allocation (accepted limitation, Feature #3502); same
 * wording as the backend review task.
 */
export const ASD_NOT_HELD_TEXT =
  "This policy uses aggregate specific deductible (ASD) allocation, which is not held: its stop-loss claims keep filing at the held deductible until the review is resolved.";

/**
 * Accessible name for the review flag. Starts with the visible label ("Review" /
 * "Review deductible") so speech users can say what they see (WCAG 2.5.3), and
 * names the claimant so a list of flags is not a list of identical names
 * (WCAG 2.4.6).
 */
export const reviewFlagAriaLabel = (claimantName?: string): string =>
  `Review deductible${claimantName?.trim() ? ` for ${claimantName.trim()}` : ""}: stop-loss claims are on hold`;

/**
 * An overdue review opened while a later trigger is in force (PO 2026-10-07: it
 * opens anyway). Recency keeps that trigger whichever candidate is chosen, so
 * the review says so (PO 2026-10-14; Quinton, coronis-apps !4879 thread 30516).
 * The latest live condition a claim line triggered, dated after every
 * candidate; undefined otherwise.
 */
export const laterTriggerInForce = (
  review: Pick<DeductibleReview, "reason" | "candidates">,
  conditions: LaseredCondition[]
): LaseredCondition | undefined => {
  if (review.reason !== "expected_trigger_overdue") return undefined;
  const latestCandidate = (review.candidates ?? []).reduce(
    (max, c) => (c.effective_date && c.effective_date.slice(0, 10) > max ? c.effective_date.slice(0, 10) : max),
    ""
  );
  return conditions
    .filter(
      (c) =>
        c.lasered_condition_trigger_state === "triggered" &&
        !!c.triggered_by_claim_item_id &&
        !!c.trigger_date &&
        c.trigger_date.slice(0, 10) > latestCandidate
    )
    .sort((a, b) => (b.trigger_date ?? "").localeCompare(a.trigger_date ?? ""))[0];
};

/** The note under the choices when a later trigger stays in force. */
export const laterTriggerText = (c: LaseredCondition): string =>
  `The later trigger (${c.lasered_condition_name}, ${money(c.amt_lasered_conditional_deductible)} from ${day(
    c.trigger_date
  )}) stays in force whichever you choose.`;
